import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { ThemeProvider } from "./context/ThemeContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SubscriptionProvider } from "./context/SubscriptionContext";
import { LanguageProvider } from "./context/LanguageContext";
import { ProfileProvider } from "./context/ProfileContext";
import { initAutoUpdate } from "./utils/autoUpdate";
import { initCapacitor } from "./utils/capacitorInit";

// Components
import Navigation from "./components/Navigation";
import MagicDust from "./components/MagicDust";
import AmbientSounds from "./components/AmbientSounds";
import Footer from "./components/Footer";
import CookieBanner from "./components/CookieBanner";

// Pages
import Library from "./pages/Library";
import StoryReader from "./pages/StoryReader";
import Login from "./pages/Login";
import Profile from "./pages/Profile";
import Upload from "./pages/Upload";
import Admin from "./pages/Admin";
import PlatformAdmin from "./pages/PlatformAdmin";
import GrandmaProfile from "./pages/GrandmaProfile";
import Subscription from "./pages/Subscription";
import TopStories from "./pages/TopStories";
import ResetPassword from "./pages/ResetPassword";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import GiftSubscription from "./pages/GiftSubscription";
import GiftSuccess from "./pages/GiftSuccess";
import OmasBibliotheek from "./pages/OmasBibliotheek";
import Garden from "./pages/Garden";
import ProfileManage from "./pages/ProfileManage";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-gold/30 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Main App Layout
const AppLayout = () => {
  // Track UTM sources once per session (e.g. ?src=garden from shared tuintjes)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const src = params.get('src');
      const ref = params.get('ref');
      if (!src) return;
      const key = `referral_tracked_${src}_${ref || ''}`;
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/referrals/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source: src, path: window.location.pathname, ref_user_id: ref || null }),
      }).catch(() => {});
    } catch {}
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <MagicDust />
      <Navigation />
      <AmbientSounds />
      
      <main className="relative z-20">
        <Routes>
          <Route path="/" element={<Library />} />
          <Route path="/story/:storyId" element={<StoryReader />} />
          <Route path="/login" element={<Login />} />
          <Route path="/omas" element={<OmasBibliotheek />} />
          <Route path="/grandma/:grandmaId" element={<GrandmaProfile />} />
          <Route path="/subscription" element={<Subscription />} />
          <Route path="/top" element={<TopStories />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          
          {/* Protected Routes */}
          <Route 
            path="/profile" 
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/upload" 
            element={
              <ProtectedRoute>
                <Upload />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin" 
            element={
              <ProtectedRoute>
                <Admin />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/platform" 
            element={
              <ProtectedRoute>
                <PlatformAdmin />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/garden" 
            element={
              <ProtectedRoute>
                <Garden />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/profielen"
            element={
              <ProtectedRoute>
                <ProfileManage />
              </ProtectedRoute>
            }
          />
          
          <Route path="/gift" element={<GiftSubscription />} />
          <Route path="/gift/success" element={<GiftSuccess />} />
          
          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      <Footer />
      <CookieBanner />
      
      <Toaster 
        position="top-center"
        toastOptions={{
          style: {
            background: 'hsl(var(--card))',
            color: 'hsl(var(--foreground))',
            border: '2px solid hsl(var(--border))',
            borderRadius: '1rem',
            fontFamily: "'Nunito', sans-serif",
          },
        }}
      />
    </div>
  );
};

function App() {
  useEffect(() => {
    initAutoUpdate();
    initCapacitor();
  }, []);

  return (
    <LanguageProvider>
      <ThemeProvider>
        <AuthProvider>
          <SubscriptionProvider>
            <ProfileProvider>
              <BrowserRouter>
                <AppLayout />
              </BrowserRouter>
            </ProfileProvider>
          </SubscriptionProvider>
        </AuthProvider>
      </ThemeProvider>
    </LanguageProvider>
  );
}

export default App;
